import React, {useContext} from "react";
import {createUseStyles, useTheme} from "react-jss";
import Countdown from "../../common/Countdown";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../../App";
import {ProcessedResponse, triviaStateDuration, TriviaStatus} from "../../../state/state";
import {QuestionDefinition} from "../../../state/questions";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import UserProvidedText from "engage-client/components/UserProvidedText";
import {useWindowSize} from "engage-client/hooks/useResizeObserver";

export const ParagraphLineHeight = 1.25;

const LgHeight = 136;
const XxlHeightProportion = 12;

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	textPrompt: {
		backgroundColor: theme.questionPrompt.background,
		width: "100%",
		maxHeight: 220, // Make sure this part doesn't use too much of the screen.
						// Number was taken from the max that fits on iPhone 5 but I think it can apply to all sizes.
						// The text will automatically size itself to fit so it'll look nicer
						// if the prompt isn't taking up way too much room.
		[theme.breakpoints.down("md")]: {
			marginTop: 56, //Give some room for the overflowing countdown and the question counter
		},
		[theme.breakpoints.down("sm")]: {
			marginTop: 46, //Give some room for the overflowing countdown and the question counter
		},
		[theme.breakpoints.down("xs")]: {
			marginTop: 36, //Give some room for the overflowing countdown and the question counter
		},
		[theme.breakpoints.up("lg")]: {
			height: LgHeight,
			borderRadius: `10px ${LgHeight / 2}px ${LgHeight / 2}px 10px`,
			display: "flex",
			flexDirection: "row-reverse", //Since we have to put the countdown first for floating on mobile
			alignItems: "center",
		},
		[theme.breakpoints.up("xxl")]: {
			maxHeight: `${XxlHeightProportion}vh`,
			height: `${XxlHeightProportion}vh`,
			borderRadius: `10px calc(${XxlHeightProportion}vh / 2) calc(${XxlHeightProportion}vh / 2) 10px`,
			display: "flex",
			flexDirection: "row-reverse", //Since we have to put the countdown first for floating on mobile
			alignItems: "center",
		}
	},
	text: {
		display: "flex",
		[theme.breakpoints.down("md")]: {
			padding: 15,
			alignItems: "flex-start"
		},
		[theme.breakpoints.up("lg")]: {
			padding: 10,
			flex: 1,
			marginLeft: 10,
			alignItems: "center"
		},
		[theme.breakpoints.up("xxl")]: {
			marginLeft: "2vw",
		}
	},
	countdown: {
		[theme.breakpoints.down("md")]: {
			float: "right",
			margin: props => `${-props.countdownSize / 2 + 4}px 10px 5px 5px`,
		},
		[theme.breakpoints.up("lg")]: {
			margin: props => (LgHeight - props.countdownSize) / 2
		},
		[theme.breakpoints.up("xxl")]: {
			margin: props => `calc((${XxlHeightProportion}vh - ${props.countdownSize}px) / 2)`
		},
	},
	li: {
		[theme.breakpoints.down("md")]: {
			listStyle: "none"
		}
	}
}));

type TextPromptProps = {
	prompt: string
}

const TextPrompt = ({prompt}:TextPromptProps) => {
	const {appState, userState} = useContext(EngageAppContext.current);
	if(appState.common) {
		const timerHidden = appState.common.gameOptions.selfTimed || appState.common.state === TriviaStatus.QuestionSummary
			|| appState.common.state === TriviaStatus.QuestionAnswer;
		const stateDuration = triviaStateDuration(appState.common);

		return <PureTextPrompt start={appState.common.timeMark} duration={stateDuration} pause={appState.common.pauseMark}
							   timerDelayed={appState.common.state !== TriviaStatus.QuestionSubmit} timerHidden={timerHidden}
							   state={appState.common.state} question={appState.common.currentQuestion}
							   playerResponse={userState.responses[appState.common.currentIndex]}
							   prompt={prompt} questionNumber={appState.common.currentIndex+1}
				/>;
	} else {
		//This is only used in storybook
		return <PureTextPrompt start={Date.now()} duration={20*1000}
							   timerDelayed={false} timerHidden={false} prompt={prompt}/>;
	}

};

type PureTextPromptProps = TextPromptProps & {
	timerDelayed: boolean //for states that shouldn't use timer
	timerHidden: boolean //for self timed
	start: number
	pause?: number
	duration: number
	question?: QuestionDefinition
	state?:TriviaStatus
	playerResponse?:ProcessedResponse
	questionNumber?: number
}

export const PureTextPrompt = (props:PureTextPromptProps) => {
	const windowHeight = useWindowSize().height;
	const theme:Theme = useTheme();
	const {prompt, start, pause, duration, timerDelayed, timerHidden, question, state, playerResponse, questionNumber} = props;
	
	const isXXLScreen = useMediaQuery(theme.breakpoints.up("xxl"));
	const isLGXLScreen = useMediaQuery(theme.breakpoints.up("lg"));
	const isMdScreen = useMediaQuery(theme.breakpoints.only("md"));
	const isSmScreen = useMediaQuery(theme.breakpoints.only("sm"));

	let countdownSize = 60;
	if(isXXLScreen && windowHeight) {
		countdownSize = (8 * windowHeight) / 100;
	} else if(isLGXLScreen) {
		countdownSize = 108;
	} else if(isMdScreen) {
		countdownSize = 90;
	} else if(isSmScreen) {
		countdownSize = 75;
	}
	const classes = useStyles({
		countdownSize,
	});

	return <div className={classes.textPrompt} data-qa="question-prompt">
		{!timerHidden ?
			<Countdown
				className={classes.countdown}
				delayed={timerDelayed}
				size={countdownSize}
				start={start}
				duration={duration}
				pause={pause}
				state={state}
				question={question}
				playerResponse={playerResponse}
			/>
			: <div className={classes.countdown} style={{height: countdownSize, width:countdownSize}}/>
		}
		<UserProvidedText maxFont={[56,44,28,24,20]} className={classes.text}><ol start={questionNumber ?? -1}><li className={classes.li}>{prompt}</li></ol></UserProvidedText>
	</div>;
};

export default TextPrompt;